import React, { useEffect, useRef, Children } from 'react';
import { motion, useInView, useMotionValue, animate } from 'framer-motion';
import { Users2Icon, TruckIcon, DollarSignIcon, TrendingUpIcon, WrenchIcon, ShoppingCartIcon } from 'lucide-react';
interface StatCard {
  prefix?: string;
  value: number;
  suffix: string;
  label: string;
  description: string;
  decimals?: number;
  icon: React.ElementType;
}
const stats: StatCard[] = [{
  value: 450,
  suffix: '+',
  label: 'Personnel Led',
  description: 'Directed multi-site aviation and manufacturing operations across global deployments',
  icon: Users2Icon
}, {
  value: 65,
  suffix: '%',
  label: 'On-Time Delivery Improvement',
  description: 'Achieved through Lean transformation and production flow redesign',
  icon: TruckIcon
}, {
  prefix: '$',
  value: 12.65,
  suffix: 'M',
  label: 'Budget Managed',
  description: 'Across 75+ automation and manufacturing projects with consistent under-budget performance',
  decimals: 2,
  icon: DollarSignIcon
}, {
  value: 80,
  suffix: '%',
  label: 'Productivity Increase',
  description: 'Delivered through workforce restructuring and automated workflow integration',
  icon: TrendingUpIcon
}, {
  value: 40,
  suffix: '%',
  label: 'Labor Cost Reduction',
  description: 'Led F/A-18 Lean Manufacturing event reducing direct labor costs across aviation operations',
  icon: WrenchIcon
}, {
  value: 33,
  suffix: '%',
  label: 'Vendor Cost Reduction',
  description: 'Renegotiated supply vendor service terms delivering sustained monthly savings',
  icon: ShoppingCartIcon
}];
interface AnimatedNumberProps {
  value: number;
  prefix?: string;
  suffix: string;
  decimals?: number;
  isInView: boolean;
}
function AnimatedNumber({
  value,
  prefix = '',
  suffix,
  decimals = 0,
  isInView
}: AnimatedNumberProps) {
  const motionValue = useMotionValue(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (isInView) {
      const controls = animate(motionValue, value, {
        duration: 2,
        ease: 'easeOut',
        onUpdate: latest => {
          if (ref.current) {
            ref.current.textContent = `${prefix}${latest.toFixed(decimals)}${suffix}`;
          }
        }
      });
      return controls.stop;
    }
  }, [isInView, motionValue, value, prefix, suffix, decimals]);
  return <span ref={ref} data-id="element-163">
      {prefix}0{suffix}
    </span>;
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const headingVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function LeadershipImpact() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="impact" ref={ref} className="py-20 relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #0a1628 0%, #0d1f4e 40%, #1a3a8f 80%, #1e4db7 100%)'
  }} data-id="element-164">
      {/* Subtle dot grid overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
      backgroundSize: '28px 28px'
    }} data-id="element-165" />

      <div className="relative z-10 max-w-5xl mx-auto px-6" data-id="element-166">
        {/* Heading */}
        <motion.div variants={headingVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="mb-14 text-center" data-id="element-167">
          <h2 className="font-display text-white" style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(28px, 4vw, 38px)',
          fontWeight: 700,
          letterSpacing: '0.05em'
        }} data-id="element-168">
            LEADERSHIP IMPACT
          </h2>
          <div className="mx-auto mt-4 h-px w-24" style={{
          background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.4), transparent)'
        }} data-id="element-169" />
          <p className="mt-4" style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          color: 'rgba(255,255,255,0.55)'
        }} data-id="element-170">
            Delivering measurable impact through strategic leadership and
            operational excellence
          </p>
        </motion.div>

        {/* Stats grid — 3 cols × 2 rows */}
        <motion.div variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-171">
          {stats.map((stat, index) => {
          const Icon = stat.icon;
          return <motion.div key={index} variants={cardVariants} className="relative flex flex-col p-6 rounded-lg overflow-hidden" style={{
            backgroundColor: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(8px)'
          }} data-id="element-172">
                {/* Top row: number + icon */}
                <div className="flex items-start justify-between mb-3" data-id="element-173">
                  <div className="font-display font-bold" style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: 'clamp(36px, 5vw, 52px)',
                color: 'white',
                lineHeight: 1,
                fontWeight: 800
              }} data-id="element-174">
                    <AnimatedNumber value={stat.value} prefix={stat.prefix} suffix={stat.suffix} decimals={stat.decimals} isInView={isInView} data-id="element-175" />
                  </div>
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0" style={{
                backgroundColor: 'rgba(255,255,255,0.12)'
              }} data-id="element-176">
                    <Icon size={18} style={{
                  color: 'rgba(255,255,255,0.8)'
                }} data-id="element-177" />
                  </div>
                </div>

                {/* Label */}
                <p className="font-semibold mb-2" style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: 'white',
              fontWeight: 700
            }} data-id="element-178">
                  {stat.label}
                </p>

                {/* Description */}
                <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '12.5px',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: '1.6'
            }} data-id="element-179">
                  {stat.description}
                </p>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}